import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Users, Building2, TrendingUp, DollarSign, 
  Target, CheckCircle2, Clock, AlertCircle, BarChart3,
  Activity, ArrowUpRight, ArrowDownRight, ExternalLink,
  Shield, Zap, AlertTriangle, Calendar, ChevronRight, Loader2
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { format, parseISO, differenceInDays } from 'date-fns';
import { AnomalyAlerts } from '@/components/ai/AIInsights';

const COLORS = ['#4A3B32', '#C6A88F', '#8B7355', '#D4C4B5', '#5D524D', '#A89080'];

// ============ CLICKABLE STAT CARD ============
function StatCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'default', onClick, testId }) {
  const colorClasses = {
    default: 'bg-white',
    success: 'bg-green-50 border-green-200',
    warning: 'bg-amber-50 border-amber-200',
    danger: 'bg-red-50 border-red-200',
    info: 'bg-blue-50 border-blue-200'
  };

  return (
    <div
      className={cn(
        "rounded-xl border border-[#E6E0D9] p-5 transition-all hover:shadow-md",
        colorClasses[color],
        onClick && "cursor-pointer hover:ring-2 hover:ring-[#4A3B32]/20"
      )}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-lg bg-[#4A3B32]/5">
          <Icon className="w-5 h-5 text-[#4A3B32]" />
        </div>
        {trend && (
          <div className={cn(
            "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
            trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          )}>
            {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trendValue}
          </div>
        )}
      </div>
      <p className="text-2xl font-heading text-[#2C2420] mb-1">{value}</p>
      <p className="text-sm text-[#5D524D]">{title}</p>
      {subtitle && <p className="text-xs text-[#8C8078] mt-1">{subtitle}</p>}
      {onClick && <p className="text-[10px] text-[#4A3B32] mt-2 flex items-center gap-1"><ChevronRight className="w-3 h-3" />Click to view details</p>}
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-xl border border-[#E6E0D9] p-5">
      <div className="mb-4">
        <h3 className="font-heading text-lg text-[#2C2420]">{title}</h3>
        {subtitle && <p className="text-sm text-[#5D524D]">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

// ============ OVERDUE TASKS DRILL-DOWN ============
function OverdueTasksModal({ open, onClose }) {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get(token, '/analytics/overdue-tasks')
        .then(res => setTasks(res.data.tasks || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, token]);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="overdue-tasks-drilldown">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            Overdue Tasks ({tasks.length})
          </SheetTitle>
        </SheetHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" /></div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12"><CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" /><p className="text-[#5D524D]">No overdue tasks!</p></div>
        ) : (
          <div className="space-y-2 mt-4">
            {tasks.map(t => {
              const daysOver = t.dueDate ? differenceInDays(new Date(), parseISO(t.dueDate)) : 0;
              return (
                <div key={t.id} className="rounded-lg border border-red-200 bg-red-50/50 p-3" data-testid={`overdue-task-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#2C2420] truncate">{t.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        <Badge className="text-[10px] bg-white/60">{t.contextType === 'lead' ? 'Lead' : 'Client'}: {t.contextName}</Badge>
                        <span className="text-red-600 font-medium">{daysOver}d overdue</span>
                      </div>
                    </div>
                    <Badge className={cn("shrink-0 text-[10px]",
                      t.priority?.toLowerCase() === 'high' ? 'bg-red-100 text-red-700' :
                      t.priority?.toLowerCase() === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                    )}>{t.priority}</Badge>
                  </div>
                  <p className="text-[10px] text-[#8C8078] mt-1.5 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{t.dueDate ? format(parseISO(t.dueDate), 'MMM d, yyyy') : 'No date'}
                    <span className="ml-2">{t.owner || t.assignedTo || 'Unassigned'}</span>
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ============ COMPLIANCE DRILL-DOWN ============
function ComplianceDrawer({ open, onClose }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('expired');

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get(token, '/analytics/compliance-overview')
        .then(res => setData(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, token]);

  const tabs = [
    { id: 'expired', label: 'Expired', count: data?.summary?.expired || 0, color: 'text-red-600' },
    { id: 'expiringSoon', label: 'Expiring Soon', count: data?.summary?.expiringSoon || 0, color: 'text-amber-600' },
    { id: 'compliant', label: 'Compliant', count: data?.summary?.compliant || 0, color: 'text-green-600' },
    { id: 'tasks', label: 'Compliance Tasks', count: data?.summary?.complianceTasks || 0, color: 'text-blue-600' },
  ];

  const items = tab === 'tasks' ? (data?.complianceTasks || []) : (data?.[tab] || []);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="compliance-drilldown">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#4A3B32]" />
            Compliance Overview
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" /></div>
        ) : !data ? (
          <p className="text-center py-8 text-[#5D524D]">No compliance data available</p>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-2 mt-4 mb-4">
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-center">
                <p className="text-2xl font-bold text-red-700">{data.summary.expired}</p>
                <p className="text-xs text-red-600">Expired</p>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{data.summary.expiringSoon}</p>
                <p className="text-xs text-amber-600">Expiring Soon</p>
              </div>
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{data.summary.compliant}</p>
                <p className="text-xs text-green-600">Compliant</p>
              </div>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{data.summary.complianceTasks}</p>
                <p className="text-xs text-blue-600">Open Tasks</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-[#E6E0D9] mb-3">
              {tabs.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    "px-3 py-2 text-xs font-medium border-b-2 transition-all",
                    tab === t.id ? `border-[#4A3B32] ${t.color}` : "border-transparent text-[#8C8078] hover:text-[#2C2420]"
                  )}
                  data-testid={`compliance-tab-${t.id}`}
                >
                  {t.label} ({t.count})
                </button>
              ))}
            </div>

            {/* Items List */}
            <div className="space-y-2">
              {items.length === 0 ? (
                <p className="text-center text-sm text-[#8C8078] py-6">No items in this category</p>
              ) : items.map((item, i) => (
                <div key={item.id || i} className="rounded-lg border border-[#E6E0D9] bg-white p-3" data-testid={`compliance-item-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-[#2C2420]">{item.title || item.name || 'Untitled'}</p>
                      <p className="text-xs text-[#5D524D] mt-0.5">{item.clientName}</p>
                    </div>
                    {item.daysUntilExpiry !== undefined && (
                      <Badge className={cn("text-[10px] shrink-0",
                        item.daysUntilExpiry < 0 ? 'bg-red-100 text-red-700' :
                        item.daysUntilExpiry <= 30 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                      )}>
                        {item.daysUntilExpiry < 0 ? `${Math.abs(item.daysUntilExpiry)}d overdue` : `${item.daysUntilExpiry}d left`}
                      </Badge>
                    )}
                    {tab === 'tasks' && (
                      <Badge className="text-[10px] bg-blue-100 text-blue-700">{item.status}</Badge>
                    )}
                  </div>
                  {(item.expiryDate || item.reviewDate || item.dueDate) && (
                    <p className="text-[10px] text-[#8C8078] mt-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {format(parseISO(item.expiryDate || item.reviewDate || item.dueDate), 'MMM d, yyyy')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ============ LEAD PIPELINE DRILL-DOWN ============
function PipelineDrillDown({ open, onClose, stageData }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="pipeline-drilldown">
        <DialogHeader>
          <DialogTitle>Lead Pipeline Breakdown</DialogTitle>
          <DialogDescription>Leads by stage with estimated values</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {(stageData || []).map((stage, i) => (
            <div key={i} className="rounded-lg border border-[#E6E0D9] p-3 flex items-center justify-between" data-testid={`pipeline-stage-${i}`}>
              <div>
                <p className="text-sm font-medium text-[#2C2420] capitalize">{stage.name}</p>
                <p className="text-xs text-[#8C8078]">{stage.count} lead{stage.count !== 1 ? 's' : ''}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-[#4A3B32]">{formatCurrency(stage.value || 0)}</p>
                <div className="h-2 w-24 bg-[#F0EBE6] rounded-full mt-1">
                  <div className="h-2 bg-[#4A3B32] rounded-full" style={{width: `${Math.min(100, (stage.count / Math.max(1, stageData.reduce((a, s) => a + s.count, 0))) * 100)}%`}} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN ANALYTICS DASHBOARD ============
export default function AnalyticsDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const [activityTrend, setActivityTrend] = useState([]);
  const [pipelineFunnel, setPipelineFunnel] = useState(null);
  const [revenueForecast, setRevenueForecast] = useState(null);
  const [adviserPerformance, setAdviserPerformance] = useState([]);
  const [cohortAnalysis, setCohortAnalysis] = useState(null);
  const [activeSection, setActiveSection] = useState('funnel');

  // Drill-down states
  const [showOverdue, setShowOverdue] = useState(false);
  const [showCompliance, setShowCompliance] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    try {
      const [analyticsRes, leadsRes, activityRes, funnelRes, forecastRes, adviserRes, cohortRes] = await Promise.all([
        api.get(token, '/analytics/overview'),
        api.get(token, '/leads/stats'),
        api.get(token, '/analytics/daily-activity'),
        api.get(token, '/analytics/pipeline-funnel'),
        api.get(token, '/analytics/revenue-forecast'),
        api.get(token, '/analytics/adviser-performance'),
        api.get(token, '/analytics/cohort-analysis'),
      ]);
      
      const analyticsData = analyticsRes.data;
      setAnalytics({
        totalClients: analyticsData.totals?.clients || 0,
        totalEntities: analyticsData.totals?.entities || 0,
        totalIndividuals: analyticsData.totals?.individuals || 0,
        totalTasks: analyticsData.totals?.tasks || 0,
        tasksDue: analyticsData.tasksDueThisWeek || 0,
        tasksOverdue: analyticsData.overdueTasks || 0,
        tasksCompletedThisMonth: analyticsData.completedTasksThisMonth || 0,
        taskCompletionRate: analyticsData.completionRate || 0,
        tasksByStatus: Object.entries(analyticsData.tasksByStatus || {}).map(([status, count]) => ({ status, count })),
        clientsBySegment: analyticsData.clientsBySegment || [],
        automationStats: analyticsData.automationStats || {},
      });
      setLeadStats(leadsRes.data);
      setActivityTrend(activityRes.data?.trend || []);
      setPipelineFunnel(funnelRes.data);
      setRevenueForecast(forecastRes.data);
      setAdviserPerformance(adviserRes.data || []);
      setCohortAnalysis(cohortRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5D524D]">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const taskCompletionData = analytics?.tasksByStatus?.map(item => ({ name: item.status, value: item.count })) || [];
  const clientSegmentData = analytics?.clientsBySegment?.map(item => ({ name: item.segment, value: item.count })) || [];
  const leadsBySourceData = leadStats?.bySource?.map(item => ({ name: item.source, count: item.count })) || [];
  const leadsByStageData = leadStats?.byStage?.map(item => ({
    name: item.stage.charAt(0).toUpperCase() + item.stage.slice(1),
    count: item.count,
    value: item.value
  })) || [];

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div>
        <h2 className="text-xl font-heading text-[#2C2420]">Analytics Overview</h2>
        <p className="text-sm text-[#5D524D]">Track your portfolio performance and team productivity</p>
      </div>

      {/* Portfolio Overview Stats */}
      <div>
        <h3 className="text-sm font-medium text-[#8C8078] uppercase tracking-wider mb-3">Portfolio Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Total Clients" value={analytics?.totalClients || 0} icon={Users} testId="stat-total-clients" />
          <StatCard title="Total Entities" value={analytics?.totalEntities || 0} icon={Building2} testId="stat-total-entities" />
          <StatCard title="Total Individuals" value={analytics?.totalIndividuals || 0} icon={Users} testId="stat-total-individuals" />
          <StatCard
            title="Compliance"
            value={<span className="flex items-center gap-1"><Shield className="w-5 h-5" />Review</span>}
            subtitle="Click to view compliance status"
            icon={Shield}
            onClick={() => setShowCompliance(true)}
            testId="stat-compliance"
          />
        </div>
      </div>

      {/* Lead Conversion Stats */}
      <div>
        <h3 className="text-sm font-medium text-[#8C8078] uppercase tracking-wider mb-3">Lead Conversion</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Active Leads" value={leadStats?.activeLeads || 0} icon={Target} color="info" testId="stat-active-leads" />
          <StatCard
            title="Pipeline Value"
            value={formatCurrency(leadStats?.pipelineValue || 0)}
            icon={DollarSign}
            onClick={() => setShowPipeline(true)}
            testId="stat-pipeline-value"
          />
          <StatCard
            title="Won This Month"
            value={leadStats?.wonThisMonth || 0}
            subtitle={formatCurrency(leadStats?.wonValueThisMonth || 0)}
            icon={CheckCircle2}
            color="success"
            testId="stat-won-month"
          />
          <StatCard
            title="Conversion Rate"
            value={`${leadStats?.conversionRate || 0}%`}
            icon={TrendingUp}
            trend={leadStats?.conversionRate > 30 ? 'up' : 'down'}
            trendValue={leadStats?.conversionRate > 30 ? 'Good' : 'Needs work'}
            testId="stat-conversion"
          />
        </div>
      </div>

      {/* Task Performance Stats */}
      <div>
        <h3 className="text-sm font-medium text-[#8C8078] uppercase tracking-wider mb-3">Task Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard title="Tasks Due" value={analytics?.tasksDue || 0} icon={Clock} color={analytics?.tasksDue > 10 ? 'warning' : 'default'} testId="stat-tasks-due" />
          <StatCard
            title="Overdue Tasks"
            value={analytics?.tasksOverdue || 0}
            icon={AlertCircle}
            color={analytics?.tasksOverdue > 0 ? 'danger' : 'success'}
            onClick={() => setShowOverdue(true)}
            testId="stat-overdue-tasks"
          />
          <StatCard title="Completed" value={analytics?.tasksCompletedThisMonth || 0} icon={CheckCircle2} color="success" testId="stat-completed" />
          <StatCard title="Completion Rate" value={`${analytics?.taskCompletionRate || 0}%`} icon={Activity} testId="stat-completion-rate" />
          <StatCard
            title="Automation"
            value={`${analytics?.automationStats?.activeRules || 0} rules`}
            subtitle={`${analytics?.automationStats?.totalExecutions || 0} executions`}
            icon={Zap}
            testId="stat-automation"
          />
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Client Segments */}
        <ChartCard title="Client Segments" subtitle="Distribution by type">
          <div className="h-64">
            {clientSegmentData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-[#8C8078] text-sm">No segment data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie data={clientSegmentData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                    {clientSegmentData.map((_, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        {/* Leads by Source */}
        <ChartCard title="Leads by Source" subtitle="Where leads come from">
          <div className="h-64">
            {leadsBySourceData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-[#8C8078] text-sm">No lead source data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leadsBySourceData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E0D9" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4A3B32" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        {/* Lead Pipeline — Clickable */}
        <ChartCard title="Lead Pipeline" subtitle="Click bars to drill into stages">
          <div className="h-64 cursor-pointer" onClick={() => setShowPipeline(true)} data-testid="pipeline-chart">
            {leadsByStageData.length === 0 ? (
              <div className="flex items-center justify-center h-full text-[#8C8078] text-sm">No pipeline data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leadsByStageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E0D9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip formatter={(val, name) => [name === 'value' ? formatCurrency(val) : val, name === 'value' ? 'Value' : 'Count']} />
                  <Bar dataKey="count" fill="#4A3B32" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ChartCard>

        {/* Activity Trend — LIVE DATA */}
        <ChartCard title="Activity Trend" subtitle="Last 7 days (live)">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={activityTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E0D9" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="activities" stroke="#4A3B32" fill="#4A3B32" fillOpacity={0.1} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      {/* Task Status Breakdown */}
      {taskCompletionData.length > 0 && (
        <ChartCard title="Task Status Breakdown" subtitle="Current status of all tasks">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie data={taskCompletionData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}>
                  {taskCompletionData.map((_, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                </Pie>
                <Tooltip />
                <Legend />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}

      {/* ============ AI ANOMALY DETECTION ============ */}
      <div className="caffeine-card p-6" data-testid="anomaly-detection-card">
        <AnomalyAlerts />
      </div>

      {/* ============ DEEPER ANALYTICS SECTION ============ */}
      <div className="border-t border-[#E6E0D9] pt-6" data-testid="deeper-analytics">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-heading text-[#2C2420]">Advanced Insights</h2>
            <p className="text-sm text-[#5D524D]">Pipeline funnel, revenue forecast, adviser performance & cohort analysis</p>
          </div>
          <div className="flex gap-1 bg-[#F0EBE6] rounded-lg p-0.5">
            {[
              { id: 'funnel', label: 'Funnel' },
              { id: 'revenue', label: 'Revenue' },
              { id: 'advisers', label: 'Advisers' },
              { id: 'cohorts', label: 'Cohorts' }
            ].map(s => (
              <button key={s.id} onClick={() => setActiveSection(s.id)}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                  activeSection === s.id ? "bg-white text-[#2C2420] shadow-sm" : "text-[#5D524D] hover:text-[#2C2420]"
                )} data-testid={`analytics-tab-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Pipeline Funnel */}
        {activeSection === 'funnel' && pipelineFunnel && (
          <div className="space-y-4" data-testid="pipeline-funnel-section">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <StatCard title="Total Pipeline" value={formatCurrency(pipelineFunnel.totalPipelineValue)} icon={DollarSign} testId="stat-total-pipeline" />
              <StatCard title="Total Leads" value={pipelineFunnel.totalLeads} icon={Target} testId="stat-total-leads-funnel" />
              <StatCard title="Active Stages" value={pipelineFunnel.stages?.filter(s => s.count > 0).length || 0} icon={BarChart3} testId="stat-active-stages" />
            </div>
            <ChartCard title="Conversion Funnel" subtitle="Leads through each pipeline stage with avg time-in-stage">
              <div className="space-y-3 py-2">
                {pipelineFunnel.stages?.filter(s => s.stage !== 'lost').map((stage, i) => {
                  const maxCount = Math.max(...pipelineFunnel.stages.map(s => s.count), 1);
                  const widthPct = Math.max(10, (stage.count / maxCount) * 100);
                  return (
                    <div key={stage.stage} className="flex items-center gap-3" data-testid={`funnel-stage-${stage.stage}`}>
                      <div className="w-24 text-right">
                        <p className="text-sm font-medium capitalize text-[#2C2420]">{stage.stage}</p>
                        <p className="text-[10px] text-[#8C8078]">{stage.probability * 100}% prob</p>
                      </div>
                      <div className="flex-1">
                        <div className="h-8 bg-[#F0EBE6] rounded-lg overflow-hidden relative">
                          <div className="h-full rounded-lg flex items-center px-3 transition-all"
                            style={{ width: `${widthPct}%`, backgroundColor: COLORS[i % COLORS.length] }}>
                            <span className="text-xs font-medium text-white whitespace-nowrap">{stage.count} leads</span>
                          </div>
                        </div>
                      </div>
                      <div className="w-24 text-right">
                        <p className="text-xs text-[#5D524D]">{formatCurrency(stage.totalValue)}</p>
                        <p className="text-[10px] text-[#8C8078]">avg {stage.avgDaysInStage}d</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Conversion rates */}
              {pipelineFunnel.conversions && Object.keys(pipelineFunnel.conversions).length > 0 && (
                <div className="mt-4 pt-3 border-t border-[#E6E0D9]">
                  <p className="text-xs font-medium text-[#8C8078] mb-2">Stage-to-Stage Conversion</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(pipelineFunnel.conversions).map(([key, rate]) => (
                      <span key={key} className="text-xs px-2 py-1 rounded-full bg-[#4A3B32]/5 text-[#4A3B32]">
                        {key.replace(/_to_/g, ' → ').replace(/_/g, ' ')}: {rate}%
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </ChartCard>
          </div>
        )}

        {/* Revenue Forecast */}
        {activeSection === 'revenue' && revenueForecast && (
          <div className="space-y-4" data-testid="revenue-forecast-section">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Existing Revenue" value={formatCurrency(revenueForecast.existingRecurring)} icon={DollarSign} subtitle="Annual recurring" testId="stat-existing-rev" />
              <StatCard title="Pipeline (Weighted)" value={formatCurrency(revenueForecast.pipelineWeightedValue)} icon={TrendingUp} subtitle="Probability-adjusted" testId="stat-weighted-pipeline" />
              <StatCard title="Best Case" value={formatCurrency(revenueForecast.pipelineBestCase)} icon={Target} subtitle="If all deals close" testId="stat-best-case" />
              <StatCard title="Monthly Projection" value={formatCurrency(revenueForecast.monthlyProjection)} icon={Calendar} subtitle="Expected new monthly" testId="stat-monthly-proj" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ChartCard title="Revenue by Segment" subtitle="Existing client revenue distribution">
                <div className="h-64">
                  {revenueForecast.revenueBySegment?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={revenueForecast.revenueBySegment} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E0D9" />
                        <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        <YAxis dataKey="segment" type="category" width={110} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                        <Bar dataKey="revenue" fill="#4A3B32" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-[#8C8078] text-sm">No segment data</div>
                  )}
                </div>
              </ChartCard>

              <ChartCard title="Top Pipeline Opportunities" subtitle="Weighted by probability">
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {revenueForecast.forecastItems?.slice(0, 8).map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-[#F9F7F5]" data-testid={`forecast-item-${i}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#2C2420] truncate">{item.name}</p>
                        <p className="text-[10px] text-[#8C8078] capitalize">{item.stage} — {item.assignedTo}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-sm font-semibold text-[#4A3B32]">{formatCurrency(item.weightedValue)}</p>
                        <p className="text-[10px] text-[#8C8078]">{(item.probability * 100).toFixed(0)}% of {formatCurrency(item.estimatedValue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ChartCard>
            </div>
          </div>
        )}

        {/* Adviser Performance */}
        {activeSection === 'advisers' && adviserPerformance.length > 0 && (
          <div className="space-y-4" data-testid="adviser-performance-section">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {adviserPerformance.map((adviser, i) => (
                <div key={adviser.id || i} className="bg-white rounded-xl border border-[#E6E0D9] p-5" data-testid={`adviser-card-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-heading text-[#2C2420]">{adviser.name}</p>
                      <p className="text-xs text-[#8C8078]">{adviser.totalClients} clients • {adviser.totalLeads} leads</p>
                    </div>
                    <div className={cn(
                      "w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold",
                      adviser.performanceScore >= 70 ? "bg-green-100 text-green-700" :
                      adviser.performanceScore >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                    )}>
                      {adviser.performanceScore}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="bg-[#F9F7F5] rounded-lg p-2">
                      <p className="text-lg font-semibold text-[#2C2420]">{adviser.completedRecent}</p>
                      <p className="text-[10px] text-[#8C8078]">Tasks (30d)</p>
                    </div>
                    <div className="bg-[#F9F7F5] rounded-lg p-2">
                      <p className="text-lg font-semibold text-[#2C2420]">{adviser.overdueTasks}</p>
                      <p className="text-[10px] text-[#8C8078]">Overdue</p>
                    </div>
                    <div className="bg-[#F9F7F5] rounded-lg p-2">
                      <p className="text-lg font-semibold text-[#2C2420]">{adviser.avgCompletionDays}d</p>
                      <p className="text-[10px] text-[#8C8078]">Avg Completion</p>
                    </div>
                    <div className="bg-[#F9F7F5] rounded-lg p-2">
                      <p className="text-lg font-semibold text-[#2C2420]">{adviser.leadsWon}</p>
                      <p className="text-[10px] text-[#8C8078]">Leads Won</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#E6E0D9] flex justify-between text-xs text-[#5D524D]">
                    <span>AUM: {formatCurrency(adviser.totalAUM)}</span>
                    <span>Revenue: {formatCurrency(adviser.totalRevenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cohort Analysis */}
        {activeSection === 'cohorts' && cohortAnalysis && (
          <div className="space-y-4" data-testid="cohort-analysis-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ChartCard title="Client Onboarding by Month" subtitle="When clients were added">
                <div className="h-64">
                  {cohortAnalysis.monthlyCohorts?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={cohortAnalysis.monthlyCohorts}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E0D9" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip formatter={(v, name) => [name === 'totalAUM' ? formatCurrency(v) : v, name === 'totalClients' ? 'Clients' : 'AUM']} />
                        <Bar dataKey="totalClients" fill="#4A3B32" radius={[4, 4, 0, 0]} name="Clients" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-[#8C8078] text-sm">No cohort data</div>
                  )}
                </div>
              </ChartCard>

              <ChartCard title="Segment Distribution" subtitle="Clients and AUM by segment">
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cohortAnalysis.segmentDistribution?.map((seg, i) => {
                    const maxCount = Math.max(...cohortAnalysis.segmentDistribution.map(s => s.count), 1);
                    return (
                      <div key={seg.segment} className="flex items-center gap-3" data-testid={`segment-row-${i}`}>
                        <div className="w-28">
                          <p className="text-sm font-medium text-[#2C2420]">{seg.segment}</p>
                          <p className="text-[10px] text-[#8C8078]">{seg.count} client{seg.count !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="flex-1">
                          <div className="h-5 bg-[#F0EBE6] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(seg.count / maxCount) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                          </div>
                        </div>
                        <div className="w-24 text-right">
                          <p className="text-xs text-[#5D524D]">{formatCurrency(seg.totalAUM)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ChartCard>

              <ChartCard title="Adviser Client Distribution" subtitle="Clients per adviser">
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cohortAnalysis.adviserDistribution?.map((adv, i) => {
                    const maxCount = Math.max(...cohortAnalysis.adviserDistribution.map(a => a.count), 1);
                    return (
                      <div key={adv.adviser} className="flex items-center gap-3" data-testid={`adviser-dist-${i}`}>
                        <div className="w-32">
                          <p className="text-sm font-medium text-[#2C2420] truncate">{adv.adviser}</p>
                        </div>
                        <div className="flex-1">
                          <div className="h-5 bg-[#F0EBE6] rounded-full overflow-hidden">
                            <div className="h-full bg-[#4A3B32] rounded-full" style={{ width: `${(adv.count / maxCount) * 100}%` }} />
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-[#5D524D]">{adv.count}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ChartCard>

              {/* Monthly Cohort AUM */}
              <ChartCard title="AUM by Onboarding Cohort" subtitle="Average AUM per onboarding month">
                <div className="h-64">
                  {cohortAnalysis.monthlyCohorts?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={cohortAnalysis.monthlyCohorts}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E0D9" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                        <Tooltip formatter={(v) => [formatCurrency(v), 'AUM']} />
                        <Area type="monotone" dataKey="totalAUM" stroke="#4A3B32" fill="#4A3B32" fillOpacity={0.1} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-[#8C8078] text-sm">No AUM data</div>
                  )}
                </div>
              </ChartCard>
            </div>
          </div>
        )}
      </div>

      {/* Drill-down Drawers & Modals */}
      <OverdueTasksModal open={showOverdue} onClose={() => setShowOverdue(false)} />
      <ComplianceDrawer open={showCompliance} onClose={() => setShowCompliance(false)} />
      <PipelineDrillDown open={showPipeline} onClose={() => setShowPipeline(false)} stageData={leadsByStageData} />
    </div>
  );
}
